import React, { useEffect, useMemo, useState } from 'react';
import { Form, Formik, FormikProps } from 'formik';
import * as Yup from 'yup';

import { CollectHtmlZone, DeclineCtaButton } from './CollectScreen.style';
import { CollectScreenConfiguration, ConsentConfiguration } from '../../common-types/ScreensConfigurations.type';
import { Background } from '../common-components/Background';
import { BlockInfoType } from '../../common-types/BlockInfo.type';
import { CtaButton } from '../common-components/CtaButton';
import { Consents } from '../common-components/Consents';
import { PersonalData } from '../common-components/PersonalData';
import { CollectFormValues } from '../../common-types/CollectForm.type';
import { Loader } from '../common-components/Loader';
import { useAppSelector } from '../../hooks';

const INVALID_EMAIL_ERROR_MESSAGE = 'Veuillez renseigner une adresse e-mail valide !';

interface Props {
  config: CollectScreenConfiguration;
  addEditableBlock?: (block: BlockInfoType) => void;
  isEdition?: boolean;
  onBlockEdit?: React.MouseEventHandler<HTMLDivElement>;
  ctaOnClick?: () => void;
  onSubmit?: (values: CollectFormValues) => void;
}

export const CollectScreen: React.FunctionComponent<Props> = ({
  addEditableBlock,
  config,
  isEdition,
  onBlockEdit,
  ctaOnClick,
  onSubmit,
} : Props) => {
  const {
    retailerPersonalData,
  } = useAppSelector((state) => state.retailerPersonalData);
  // let's discuss it for smart optins:
  //  - when I got en email (only asking an email) and no optins to ask (from retailerData)
  //    => auto validate to go to next screen
  const [autoSubmitChecked, setAutoSubmitChecked] = useState<boolean>(false);
  useEffect(() => {
    const hasEmailAndNoOptins = typeof onSubmit === 'function' && retailerPersonalData !== null &&
      retailerPersonalData.email !== null &&
      Array.isArray(retailerPersonalData.optinList) &&
      retailerPersonalData.optinList.length === 0;
    if (retailerPersonalData !== null && hasEmailAndNoOptins) {
      onSubmit({
        consents: {},
        personalData: { email: retailerPersonalData.email as string },
      });
      return;
    }
    setAutoSubmitChecked(true);
  }, [onSubmit, retailerPersonalData]);


  const personalDataShape = useMemo(() => {
    const shape: Record<string, Yup.AnySchema> = {};
    const filteredPersonalData = config.personalData.filter(({ activated }) => activated);
    for (const { type, key, required } of filteredPersonalData) {
      switch (type) {
      case 'email':
        shape[key] = Yup.string().matches(
          // eslint-disable-next-line max-len
          /^(?:[A-Z|a-z|0-9](?:\.|_|-|!|#|\$|%|&|'|\*|\+|-|\/|=|\?|\^|_|`|\{|\||\}|~){0,1})+[A-Z|a-z|0-9]@(?:[A-Z|a-z|0-9])+(?:(?:\.|-){0,1}[A-Z|a-z|0-9]{2})+\.[a-z]{2,3}$/gui,
          INVALID_EMAIL_ERROR_MESSAGE
        );
        break;
      default:
        shape[key] = Yup.mixed();
        break;
      }
      if (required === true) {
        shape[key] = shape[key].required(INVALID_EMAIL_ERROR_MESSAGE);
      }
    }
    return shape;
  }, [config.personalData]);

  const userConsents = useMemo<ConsentConfiguration[]>(() => {
    const retailerOptinKeys = retailerPersonalData === null || retailerPersonalData.optinList === null
      ? null
      : retailerPersonalData.optinList.map((optin) => optin.key);
    // activated AND (no retailerOptins OR consent in list)
    return config.consents.filter(({ activated, key }) => activated && (retailerOptinKeys === null || retailerOptinKeys.includes(key)));
  }, [config.consents, retailerPersonalData]);

  const CollectScreenSchema = Yup.object().shape({
    personalData: Yup.object().shape(personalDataShape),
  });

  const handleDeclineCtaClick = ({ handleSubmit, setFieldValue }: Pick<FormikProps<CollectFormValues>, 'handleSubmit' | 'setFieldValue'>): void => {
    userConsents.forEach(({ key }) => {
      setFieldValue(`consents.${key}`, false);
    });
    handleSubmit();
  };

  const handleAcceptCtaClick = ({ handleSubmit, setFieldValue }: Pick<FormikProps<CollectFormValues>, 'handleSubmit' | 'setFieldValue'>): void => {
    userConsents.forEach(({ key }) => {
      setFieldValue(`consents.${key}`, true);
    });
    handleSubmit();
  };

  const isConsentsDisabled = (values: CollectFormValues) : boolean => {
    const email = config.personalData.find(({ key, activated, required }) => key === 'email' && activated === true && required === true);
    return email === undefined
      ? false
      : values.personalData.email === undefined || values.personalData.email.length === 0;
  };
  if (autoSubmitChecked !== true) {
    return <Loader/>;
  }
  return (
    <Background
      displayName="Background"
      sizeInfo={{ height: 1200, width: 1200 }}
      addEditableBlock={addEditableBlock}
      dataTemplateConfig="screens.collect.background"
      backgroundImage={config.background.imageUrl}
      backgroundColor={config.background.color}
      isEdition={isEdition}
      onClick={onBlockEdit}
    >
      <CollectHtmlZone
        displayName="HTML Zone"
        addEditableBlock={addEditableBlock}
        html={config.mainTextBox.html}
        dataTemplateConfig="screens.collect.mainTextBox"
        isEdition={isEdition}
        onClick={onBlockEdit}
      />
      <Formik
        initialValues={{
          consents: {},
          personalData: {
            email: retailerPersonalData === null || retailerPersonalData.email === null
              ? ''
              : retailerPersonalData.email,
          },
        }}
        validationSchema={CollectScreenSchema}
        validateOnChange={true}
        onSubmit={(values, { setSubmitting }): void => {
          if (onSubmit !== undefined) {
            onSubmit(values);
          }
          if (ctaOnClick !== undefined) {
            ctaOnClick();
          }
          setSubmitting(false);
        }}
      >
        {({ handleSubmit, setFieldValue, values, isSubmitting }: FormikProps<CollectFormValues>): React.ReactNode => <Form
          onKeyDown={(keyEvent): void => {
            if (keyEvent.key === 'Enter') {
              keyEvent.preventDefault();
            }
          }}
        >
          <PersonalData
            displayName="Personal Data"
            dataTemplateConfig="screens.collect.personalData"
            personalDataConfig={config.personalData}
            isEdition={isEdition}
            addEditableBlock={addEditableBlock}
            onBlockEdit={onBlockEdit}
            availableData={{ email: retailerPersonalData?.email }}
          />
          <Consents
            disabled={isConsentsDisabled(values)}
            isEdition={isEdition}
            consentsConfig={userConsents}
            onBlockEdit={onBlockEdit}
            dataTemplateConfig="screens.collect.consents"
            displayName="Consents"
          />
          <DeclineCtaButton
            disabled={isSubmitting || isConsentsDisabled(values)}
            dataTemplateType="decline-cta-button"
            displayName="Opt-out CTA"
            addEditableBlock={addEditableBlock}
            innerHTML={config.declineCtaButton.html}
            onClick={
              onBlockEdit === undefined
                ? (): void => handleDeclineCtaClick({ handleSubmit, setFieldValue })
                : onBlockEdit
            }
            dataTemplateConfig="screens.collect.declineCtaButton"
            isEdition={isEdition}
            backgroundColor={config.declineCtaButton.background.color}
          />
          <CtaButton
            disabled={isSubmitting || isConsentsDisabled(values)}
            dataTemplateType="accept-cta-button"
            displayName="Accept CTA"
            addEditableBlock={addEditableBlock}
            backgroundColor={config.acceptCtaButton.background.color}
            innerHTML={config.acceptCtaButton.html}
            onClick={
              onBlockEdit === undefined
                ? (): void => handleAcceptCtaClick({ handleSubmit, setFieldValue })
                : onBlockEdit
            }
            dataTemplateConfig="screens.collect.acceptCtaButton"
            isEdition={isEdition}
          />
        </Form>
        }
      </Formik>
    </Background>
  );
};
